import { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import { getBrand } from '../constants/brands';
import {
  forgetPolicyCache,
  loadPolicyCache,
  readPolicyCache,
  sha256Hex,
  writePolicyCache,
  type CachedPolicy,
} from '../../config/policyCache';
import { POLICY_DOCUMENT, POLICY_VERSION } from '../services/policyService/queries';

/** Spec 031: with nothing cached, the static file waits this long for the API. */
export const POLICY_FALLBACK_MS = 1500;

export type PolicySource = 'api' | 'cache' | 'file';

export type PolicyDocumentState = {
  /** Raw markdown. Empty while nothing can be shown yet. */
  text: string;
  versionId: string | null;
  versionNumber: number | null;
  effectiveDate: string | null;
  title: string | null;
  approximateDates: boolean;
  source: PolicySource | null;
  /** False while there is nothing to show (no cache, API still in the wait). */
  ready: boolean;
  error: string | null;
};

type VersionShape = {
  id?: string | null;
  title?: string | null;
  versionNumber?: number | null;
  effectiveDate?: string | null;
  contentMarkdown?: string | null;
  contentSha256?: string | null;
  approximateDates?: boolean | null;
};

function usable(version: VersionShape | null | undefined): version is VersionShape & {
  id: string;
  contentMarkdown: string;
} {
  return Boolean(version?.id && version.contentMarkdown && version.contentMarkdown.trim());
}

function fromVersion(version: VersionShape, source: PolicySource): PolicyDocumentState {
  return {
    text: version.contentMarkdown || '',
    versionId: version.id || null,
    versionNumber: typeof version.versionNumber === 'number' ? version.versionNumber : null,
    effectiveDate: version.effectiveDate || null,
    title: version.title || null,
    approximateDates: version.approximateDates === true,
    source,
    ready: true,
    error: null,
  };
}

function fromCache(entry: CachedPolicy): PolicyDocumentState {
  return {
    text: entry.text,
    versionId: entry.versionId,
    versionNumber: entry.versionNumber,
    effectiveDate: entry.effectiveDate || null,
    title: entry.title || null,
    approximateDates: entry.approximateDates,
    source: 'cache',
    ready: true,
    error: null,
  };
}

const EMPTY: PolicyDocumentState = {
  text: '',
  versionId: null,
  versionNumber: null,
  effectiveDate: null,
  title: null,
  approximateDates: false,
  source: null,
  ready: false,
  error: null,
};

/**
 * Spec 031: the current policy (or one old version) from the API, the browser
 * cache, then the static file the policy page already fetches.
 * The cache paints immediately. The file is used when the API errors or has
 * not answered within 1.5s and there is nothing cached.
 */
export function usePolicyDocument(options: {
  kind: string;
  contentPath: string | null;
  brandSite?: string;
  /** Load `policyVersion(id)` instead of the current document. Not cached. */
  versionId?: string | null;
}): PolicyDocumentState {
  const brandSite = options.brandSite ?? getBrand().brandSite;
  const historical = Boolean(options.versionId);
  const [cached, setCached] = useState<CachedPolicy | null>(() =>
    historical ? null : readPolicyCache(brandSite, options.kind),
  );
  const [timedOut, setTimedOut] = useState(false);
  const [fileText, setFileText] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);

  const currentQuery = useQuery(POLICY_DOCUMENT, {
    variables: { kind: options.kind },
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
    skip: historical,
  });
  const versionQuery = useQuery(POLICY_VERSION, {
    variables: { id: options.versionId ?? '' },
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
    skip: !historical,
  });
  const query = historical ? versionQuery : currentQuery;
  const apiVersion: VersionShape | null = historical
    ? (query.data?.policyVersion ?? null)
    : (query.data?.policyDocument ?? null);
  const apiReady = usable(apiVersion);
  const apiFailed = Boolean(query.error) && !apiReady;

  useEffect(() => {
    if (historical || !cached) return undefined;
    let cancelled = false;
    sha256Hex(cached.text).then(hash => {
      if (cancelled || !hash || hash === cached.hash) return;
      forgetPolicyCache(brandSite, options.kind);
      setCached(null);
    });
    return () => {
      cancelled = true;
    };
  }, [cached, brandSite, historical, options.kind]);

  useEffect(() => {
    if (apiReady || cached) return undefined;
    const timer = setTimeout(() => setTimedOut(true), POLICY_FALLBACK_MS);
    return () => clearTimeout(timer);
  }, [apiReady, cached]);

  const fetchFile = !apiReady && !cached && (timedOut || apiFailed);

  useEffect(() => {
    if (!fetchFile || !options.contentPath) return undefined;
    let cancelled = false;
    setFileError(null);
    fetch(options.contentPath)
      .then(response => {
        if (!response.ok) throw new Error('Failed to load this document');
        return response.text();
      })
      .then(text => {
        if (!cancelled) setFileText(text);
      })
      .catch(() => {
        if (!cancelled) setFileError('Unable to load this document. Please try again later.');
      });
    return () => {
      cancelled = true;
    };
  }, [fetchFile, options.contentPath]);

  useEffect(() => {
    if (!apiReady || historical || !apiVersion) return;
    writePolicyCache(brandSite, options.kind, {
      versionId: apiVersion.id,
      versionNumber: typeof apiVersion.versionNumber === 'number' ? apiVersion.versionNumber : null,
      effectiveDate: apiVersion.effectiveDate || '',
      title: apiVersion.title || '',
      text: apiVersion.contentMarkdown,
      hash: apiVersion.contentSha256 || '',
      approximateDates: apiVersion.approximateDates === true,
    });
  }, [apiReady, apiVersion, brandSite, historical, options.kind]);

  if (apiReady && apiVersion) return fromVersion(apiVersion, 'api');
  if (cached) return fromCache(cached);
  if (fileText) {
    return {
      ...EMPTY,
      text: fileText,
      source: 'file',
      ready: true,
    };
  }
  if (fileError || (apiFailed && !options.contentPath)) {
    return {
      ...EMPTY,
      ready: true,
      error: fileError || 'Unable to load this document. Please try again later.',
    };
  }
  return EMPTY;
}

/** Drop a bad cache entry. Exported so tests can await the same check the hook runs. */
export async function verifiedPolicyCache(
  brandSite: string,
  kind: string,
  storage?: Parameters<typeof loadPolicyCache>[2],
): Promise<CachedPolicy | null> {
  return loadPolicyCache(brandSite, kind, storage);
}
